import { ReactElement, useEffect } from 'react';
import { FiltersMap } from '@wix/bex-core';
import { runInAction } from 'mobx';
import { useToolbarCollectionSyncProps } from '../ToolbarCollection/useToolbarCollectionSyncProps';
import { SupportedCardGalleryItemProps } from './SupportedCardGalleryItemProps';
import { ActionsCell } from '../ActionsCell';
import { gridSizePresets, GridState } from '../../state';
import { AddItemProps } from '@wix/design-system';
import { DragAndDrop } from '../DragAndDrop';
import { useScrollableContentContext } from '../../providers';

export interface UseGridSyncPropsParams<T, F extends FiltersMap> {
  state: GridState<T, F>;

  preset?: 'full' | 'title' | 'empty';

  renderItem?: (
    item: T,
    index: number,
  ) => Partial<SupportedCardGalleryItemProps>;

  actionsCell?: ActionsCell<T, F>;

  renderAddItem?: () => ReactElement<AddItemProps>;
  dragAndDrop?: DragAndDrop;

  isUsingFolders?: boolean;

  imagePlacement?: 'side' | 'top';
}

export function useGridSyncProps<T, F extends FiltersMap>(
  props: UseGridSyncPropsParams<T, F>,
) {
  const { state, renderAddItem, preset, dragAndDrop, isUsingFolders } = props;
  const { scrollableContentRef } = useScrollableContentContext();

  useToolbarCollectionSyncProps({
    ...props,
    state: state.toolbar,
    isUsingDragAndDrop: !!props.dragAndDrop,
    isUsingBulkActionsToolbar: false,
    isUsingHorizontalScroll: false,
    isUsingFolders: isUsingFolders ?? false,
    isUsingTableVirtualization: false,
  });

  useEffect(() => {
    const { collection } = state;

    const scrollableContent = scrollableContentRef?.current;

    if (scrollableContent) {
      collection.scrollableContent = scrollableContent;
    }

    return () => {
      collection.scrollableContent = null;
    };
  }, []);

  useEffect(() => {
    runInAction(() => {
      state.trailingItemsCount = [renderAddItem].filter(Boolean).length;
    });
  }, [renderAddItem]);

  useEffect(() => {
    runInAction(() => {
      state.preset = preset ?? 'title';
    });
  }, [preset]);

  useEffect(() => {
    runInAction(() => {
      state._draggable = dragAndDrop ? 'draggable' : 'none';
    });
  }, [dragAndDrop]);

  useEffect(() => {
    runInAction(() => {
      if (!props.imagePlacement || !gridSizePresets[props.imagePlacement]) {
        return;
      }
      state.sizePreset = gridSizePresets[props.imagePlacement];
    });
  }, []);
}
